import { useEffect, useRef, useState, useMemo } from "react";
import {
  Users, Plus, Pencil, Trash2, Save, X, ChevronUp, ChevronDown,
  Loader2, Upload, ImageIcon, GripVertical,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

const EMPTY_GROUP = { name_es: "", name_en: "", order: 0 };
const EMPTY_MEMBER = {
  group_id: "",
  full_name: "",
  role_es: "",
  role_en: "",
  bio_es: "",
  bio_en: "",
  image_url: "",
  order: 0,
};

/**
 * AdminTeam — /admin/team
 *
 * Manage the "Meet Our Church" directory. Admin can:
 *   * Create/rename/reorder/delete groups (Pastor, Worship Team, Council, ...)
 *   * Add/edit/delete members with photo upload (device or paste URL),
 *     bilingual role + bio, and per-group ordering.
 * Deleting a group cascades its members on the backend.
 */
export default function AdminTeam() {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ groups: [] });

  const [groupOpen, setGroupOpen] = useState(false);
  const [groupEditing, setGroupEditing] = useState(EMPTY_GROUP);
  const [groupDeleteTarget, setGroupDeleteTarget] = useState(null);

  const [memberOpen, setMemberOpen] = useState(false);
  const [memberEditing, setMemberEditing] = useState(EMPTY_MEMBER);
  const [memberDeleteTarget, setMemberDeleteTarget] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data: teamData } = await api.get("/team");
      setData(teamData || { groups: [] });
    } catch (err) {
      console.error("AdminTeam: failed to load", err);
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const groups = data?.groups || [];

  // ---- Groups ---------------------------------------------------------------
  const onGroupSave = async () => {
    const g = groupEditing;
    if (!g.name_es.trim() || !g.name_en.trim()) {
      toast.error(lang === "es" ? "Falta el nombre en ambos idiomas." : "Name required in both languages.");
      return;
    }
    try {
      if (g.id) {
        await api.put(`/admin/team/groups/${g.id}`, {
          name_es: g.name_es.trim(), name_en: g.name_en.trim(), order: Number(g.order) || 0,
        });
      } else {
        await api.post("/admin/team/groups", {
          name_es: g.name_es.trim(), name_en: g.name_en.trim(), order: Number(g.order) || 0,
        });
      }
      toast.success(t.admin.saved);
      setGroupOpen(false);
      reload();
    } catch (err) {
      toast.error(t.common.error);
    }
  };

  const onGroupDelete = async () => {
    if (!groupDeleteTarget) return;
    try {
      await api.delete(`/admin/team/groups/${groupDeleteTarget.id}`);
      toast.success(t.admin.deleted);
      reload();
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setGroupDeleteTarget(null);
    }
  };

  const moveGroup = async (g, direction) => {
    // Compute the desired new order by swapping with the neighbor.
    const sorted = [...groups].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex((x) => x.id === g.id);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || targetIdx < 0 || targetIdx >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[targetIdx];
    try {
      // Swap order values then persist both.
      await Promise.all([
        api.put(`/admin/team/groups/${a.id}`, { name_es: a.name_es, name_en: a.name_en, order: b.order || 0 }),
        api.put(`/admin/team/groups/${b.id}`, { name_es: b.name_es, name_en: b.name_en, order: a.order || 0 }),
      ]);
      reload();
    } catch (err) {
      toast.error(t.common.error);
    }
  };

  // ---- Members --------------------------------------------------------------
  const startAddMember = (group) => {
    setMemberEditing({ ...EMPTY_MEMBER, group_id: group.id });
    setMemberOpen(true);
  };

  const startEditMember = (m) => {
    setMemberEditing({ ...m });
    setMemberOpen(true);
  };

  const onMemberUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const token = (typeof localStorage !== "undefined" && localStorage.getItem("ncl_admin_token")) || "";
      const res = await fetch(`${API_BASE}/admin/team/upload-image`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!res.ok) throw new Error("upload failed");
      const json = await res.json();
      setMemberEditing((prev) => ({ ...prev, image_url: json.image_url }));
      toast.success(lang === "es" ? "Foto subida" : "Photo uploaded");
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onMemberSave = async () => {
    const m = memberEditing;
    if (!m.group_id) {
      toast.error(lang === "es" ? "Falta el grupo." : "Group required.");
      return;
    }
    if (!m.full_name.trim()) {
      toast.error(lang === "es" ? "Falta el nombre." : "Name required.");
      return;
    }
    if (!m.image_url) {
      toast.error(lang === "es" ? "Falta la foto." : "Photo required.");
      return;
    }
    const payload = {
      group_id: m.group_id,
      full_name: m.full_name.trim(),
      role_es: (m.role_es || "").trim(),
      role_en: (m.role_en || "").trim(),
      bio_es: (m.bio_es || "").trim(),
      bio_en: (m.bio_en || "").trim(),
      image_url: m.image_url,
      order: Number(m.order) || 0,
    };
    try {
      if (m.id) await api.put(`/admin/team/members/${m.id}`, payload);
      else await api.post("/admin/team/members", payload);
      toast.success(t.admin.saved);
      setMemberOpen(false);
      reload();
    } catch (err) {
      toast.error(t.common.error);
    }
  };

  const onMemberDelete = async () => {
    if (!memberDeleteTarget) return;
    try {
      await api.delete(`/admin/team/members/${memberDeleteTarget.id}`);
      toast.success(t.admin.deleted);
      reload();
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setMemberDeleteTarget(null);
    }
  };

  const moveMember = async (member, direction) => {
    const group = groups.find((g) => g.id === member.group_id);
    const siblings = [...(group?.members || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = siblings.findIndex((x) => x.id === member.id);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (idx < 0 || targetIdx < 0 || targetIdx >= siblings.length) return;
    const a = siblings[idx];
    const b = siblings[targetIdx];
    try {
      await Promise.all([
        api.put(`/admin/team/members/${a.id}`, { ...a, order: b.order || 0 }),
        api.put(`/admin/team/members/${b.id}`, { ...b, order: a.order || 0 }),
      ]);
      reload();
    } catch (err) {
      toast.error(t.common.error);
    }
  };

  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-5" data-testid="admin-team-page">
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="font-serif-display text-2xl font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            {lang === "es" ? "Conoce nuestra iglesia" : "Meet our church"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {lang === "es"
              ? "Organiza a las personas que sirven en la iglesia por grupos (Pastor, alabanza, concilio, etc.)."
              : "Organize the people who serve at the church into groups (Pastor, worship team, council, etc.)."}
          </p>
        </div>
        <Button
          onClick={() => { setGroupEditing(EMPTY_GROUP); setGroupOpen(true); }}
          data-testid="team-add-group"
        >
          <Plus className="mr-1 h-4 w-4" />
          {lang === "es" ? "Nuevo grupo" : "New group"}
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full rounded-2xl" />
          ))}
        </div>
      ) : groups.length === 0 ? (
        <Card className="rounded-2xl ring-1 ring-black/5 shadow-sm">
          <CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="team-empty-groups">
            {lang === "es"
              ? "No hay grupos aun. Crea uno para empezar (por ejemplo: Pastor, Equipo de alabanza, Concilio)."
              : "No groups yet. Create one to start (e.g. Pastor, Worship team, Church council)."}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {groups.map((g, gi) => (
            <Card key={g.id} className="rounded-2xl ring-1 ring-black/5 shadow-sm overflow-hidden" data-testid={`team-group-${g.id}`}>
              <CardContent className="p-0">
                {/* Group header */}
                <div className="flex items-center gap-2 border-b p-4">
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveGroup(g, "up")}
                      disabled={gi === 0}
                      className="text-muted-foreground disabled:opacity-30 hover:text-foreground"
                      aria-label="move up"
                      data-testid={`team-group-up-${g.id}`}
                    >
                      <ChevronUp className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => moveGroup(g, "down")}
                      disabled={gi === groups.length - 1}
                      className="text-muted-foreground disabled:opacity-30 hover:text-foreground"
                      aria-label="move down"
                      data-testid={`team-group-down-${g.id}`}
                    >
                      <ChevronDown className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-serif-display text-lg font-semibold truncate">
                      {lang === "es" ? g.name_es : g.name_en}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      ES: {g.name_es} · EN: {g.name_en} · #{g.order || 0}
                    </p>
                  </div>
                  <Badge variant="secondary" className="rounded-full text-[10px]">
                    {(g.members || []).length}
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setGroupEditing({ ...g }); setGroupOpen(true); }}
                    data-testid={`team-group-edit-${g.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setGroupDeleteTarget(g)}
                    aria-label="delete group"
                    data-testid={`team-group-delete-${g.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>

                {/* Members list */}
                <div className="divide-y">
                  {(g.members || []).length === 0 ? (
                    <div className="p-4 text-center text-xs text-muted-foreground">
                      {lang === "es" ? "Aun no hay personas en este grupo." : "No people in this group yet."}
                    </div>
                  ) : (
                    (g.members || []).map((m, mi, arr) => (
                      <div key={m.id} className="flex items-start gap-3 p-3" data-testid={`team-member-${m.id}`}>
                        <div className="flex flex-col">
                          <button
                            onClick={() => moveMember(m, "up")}
                            disabled={mi === 0}
                            className="text-muted-foreground disabled:opacity-30 hover:text-foreground"
                            aria-label="move up"
                          >
                            <ChevronUp className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => moveMember(m, "down")}
                            disabled={mi === arr.length - 1}
                            className="text-muted-foreground disabled:opacity-30 hover:text-foreground"
                            aria-label="move down"
                          >
                            <ChevronDown className="h-4 w-4" />
                          </button>
                        </div>
                        <img
                          src={m.image_url}
                          alt={m.full_name}
                          className="h-14 w-14 rounded-full object-cover ring-1 ring-black/5"
                          onError={(e) => { e.currentTarget.style.opacity = "0.4"; }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium truncate">{m.full_name}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {lang === "es" ? (m.role_es || m.role_en) : (m.role_en || m.role_es)}
                          </p>
                          {(m.bio_es || m.bio_en) && (
                            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                              {lang === "es" ? (m.bio_es || m.bio_en) : (m.bio_en || m.bio_es)}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => startEditMember(m)}
                          data-testid={`team-member-edit-${m.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setMemberDeleteTarget(m)}
                          aria-label="delete member"
                          data-testid={`team-member-delete-${m.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                <div className="border-t p-3 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => startAddMember(g)}
                    data-testid={`team-add-member-${g.id}`}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    {lang === "es" ? "Agregar persona" : "Add person"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Group dialog */}
      <Dialog open={groupOpen} onOpenChange={setGroupOpen}>
        <DialogContent className="max-w-md" data-testid="team-group-dialog">
          <DialogHeader>
            <DialogTitle>
              {groupEditing.id
                ? (lang === "es" ? "Editar grupo" : "Edit group")
                : (lang === "es" ? "Nuevo grupo" : "New group")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{lang === "es" ? "Nombre (ES)" : "Name (ES)"}</Label>
              <Input
                value={groupEditing.name_es}
                onChange={(e) => setGroupEditing({ ...groupEditing, name_es: e.target.value })}
                placeholder={lang === "es" ? "Ej. Equipo de alabanza" : "e.g. Worship team"}
                data-testid="team-group-name-es"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{lang === "es" ? "Nombre (EN)" : "Name (EN)"}</Label>
              <Input
                value={groupEditing.name_en}
                onChange={(e) => setGroupEditing({ ...groupEditing, name_en: e.target.value })}
                placeholder="e.g. Worship team"
                data-testid="team-group-name-en"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{lang === "es" ? "Orden (0 primero)" : "Order (0 first)"}</Label>
              <Input
                type="number"
                value={groupEditing.order}
                onChange={(e) => setGroupEditing({ ...groupEditing, order: Number(e.target.value) })}
                data-testid="team-group-order"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupOpen(false)}>
              <X className="mr-1 h-4 w-4" />{lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button onClick={onGroupSave} data-testid="team-group-save">
              <Save className="mr-1 h-4 w-4" />{lang === "es" ? "Guardar" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete group confirmation */}
      <AlertDialog open={!!groupDeleteTarget} onOpenChange={(o) => { if (!o) setGroupDeleteTarget(null); }}>
        <AlertDialogContent data-testid="team-group-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {lang === "es" ? "\u00bfEliminar este grupo?" : "Delete this group?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {lang === "es"
                ? "Se borraran tambien las personas dentro del grupo. Esta accion no se puede deshacer."
                : "This also deletes the people in this group. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lang === "es" ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={onGroupDelete}>
              {lang === "es" ? "Eliminar" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Member dialog */}
      <Dialog open={memberOpen} onOpenChange={setMemberOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="team-member-dialog">
          <DialogHeader>
            <DialogTitle>
              {memberEditing.id
                ? (lang === "es" ? "Editar persona" : "Edit person")
                : (lang === "es" ? "Nueva persona" : "New person")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Photo */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">
                {lang === "es" ? "Foto" : "Photo"} <span className="text-destructive">*</span>
              </Label>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 shrink-0 rounded-full ring-1 ring-black/10 bg-muted/40 overflow-hidden flex items-center justify-center">
                  {memberEditing.image_url ? (
                    <img
                      src={memberEditing.image_url}
                      alt="preview"
                      className="h-full w-full object-cover"
                      data-testid="team-member-photo-preview"
                    />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0 space-y-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={onMemberUpload}
                    className="hidden"
                    data-testid="team-member-photo-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="team-member-photo-upload-btn"
                  >
                    {uploading
                      ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      : <Upload className="mr-1 h-4 w-4" />}
                    {lang === "es" ? "Subir desde el dispositivo" : "Upload from device"}
                  </Button>
                  <Input
                    value={memberEditing.image_url || ""}
                    onChange={(e) => setMemberEditing({ ...memberEditing, image_url: e.target.value })}
                    placeholder={lang === "es" ? "\u2026 o pega una URL de imagen" : "\u2026 or paste an image URL"}
                    data-testid="team-member-photo-url"
                  />
                </div>
              </div>
            </div>

            {/* Group + name + order */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="space-y-1.5 sm:col-span-2">
                <Label className="text-xs font-medium">{lang === "es" ? "Grupo" : "Group"}</Label>
                <select
                  value={memberEditing.group_id}
                  onChange={(e) => setMemberEditing({ ...memberEditing, group_id: e.target.value })}
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  data-testid="team-member-group"
                >
                  <option value="">{lang === "es" ? "Seleccionar grupo..." : "Select group..."}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {lang === "es" ? g.name_es : g.name_en}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Orden" : "Order"}</Label>
                <Input
                  type="number"
                  value={memberEditing.order}
                  onChange={(e) => setMemberEditing({ ...memberEditing, order: Number(e.target.value) })}
                  data-testid="team-member-order"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium">{lang === "es" ? "Nombre completo" : "Full name"}</Label>
              <Input
                value={memberEditing.full_name}
                onChange={(e) => setMemberEditing({ ...memberEditing, full_name: e.target.value })}
                placeholder={lang === "es" ? "Ej. Juan Perez" : "e.g. Jane Doe"}
                data-testid="team-member-name"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Titulo (ES)" : "Title (ES)"}</Label>
                <Input
                  value={memberEditing.role_es}
                  onChange={(e) => setMemberEditing({ ...memberEditing, role_es: e.target.value })}
                  placeholder={lang === "es" ? "Ej. Pastor Principal" : "e.g. Pastor Principal"}
                  data-testid="team-member-role-es"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Titulo (EN)" : "Title (EN)"}</Label>
                <Input
                  value={memberEditing.role_en}
                  onChange={(e) => setMemberEditing({ ...memberEditing, role_en: e.target.value })}
                  placeholder="e.g. Lead Pastor"
                  data-testid="team-member-role-en"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Biografia corta (ES)" : "Short bio (ES)"}</Label>
                <Textarea
                  value={memberEditing.bio_es}
                  rows={3}
                  onChange={(e) => setMemberEditing({ ...memberEditing, bio_es: e.target.value })}
                  data-testid="team-member-bio-es"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">{lang === "es" ? "Biografia corta (EN)" : "Short bio (EN)"}</Label>
                <Textarea
                  value={memberEditing.bio_en}
                  rows={3}
                  onChange={(e) => setMemberEditing({ ...memberEditing, bio_en: e.target.value })}
                  data-testid="team-member-bio-en"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberOpen(false)}>
              <X className="mr-1 h-4 w-4" />{lang === "es" ? "Cancelar" : "Cancel"}
            </Button>
            <Button onClick={onMemberSave} disabled={uploading} data-testid="team-member-save">
              <Save className="mr-1 h-4 w-4" />{lang === "es" ? "Guardar" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete member confirmation */}
      <AlertDialog open={!!memberDeleteTarget} onOpenChange={(o) => { if (!o) setMemberDeleteTarget(null); }}>
        <AlertDialogContent data-testid="team-member-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {lang === "es" ? "\u00bfEliminar esta persona?" : "Delete this person?"}
            </AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{lang === "es" ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={onMemberDelete}>
              {lang === "es" ? "Eliminar" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
